import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  tabClasses,
  Tabs,
  tabsClasses,
  Typography,
} from '@mui/material';
import { useContext, useState } from 'react';
import { Link } from 'react-router';

import { MAIN_CONTENT_ID, TAB_BOX_SHADOW_WIDTH } from '../constants';
import DashboardContext from './Dashboard/DashboardContext';

export default function Header() {
  const context = useContext(DashboardContext);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  return (
    <Stack
      component="header"
      direction="row"
      sx={{
        display: 'flex',
        alignItems: { xs: 'center', md: 'flex-start' },
        justifyContent: { xs: 'space-between', md: 'flex-start' },
        boxShadow: '3px 3px 3px rgba(0, 0, 0, 0.1)',
        gap: { xs: 0, sm: 2 },
        top: 0,
        pb: { xs: 0.5, md: 0 },
        pt: { xs: 0.5, md: 1.5, lg: 2 },
        px: { md: '7%', lg: '10.4%', xl: '12%' },
        pr: { xs: 0.5 },
        pl: { xs: 1 },
        position: 'sticky',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Button
        href={`#${MAIN_CONTENT_ID}`}
        onClick={() => {
          document.getElementById(MAIN_CONTENT_ID)?.focus();
        }}
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          color: 'text.primary',
          right: 10,
          top: 10,
          position: 'absolute',
          // Initially hide the button above the header, reveal on focus
          transform: 'translateY(-135%)',
          '&:focus': {
            transform: 'translateY(0)',
          },
        }}
      >
        Skip to main content
      </Button>
      <Button
        href="/"
        sx={{
          height: { xs: 60, lg: 80 },
          minWidth: 0,
          mb: { xs: 0, lg: 2 },
          p: 0,
        }}
      >
        <Box
          component="img"
          aria-label={`${context.organizationName} home`}
          alt={`${context.organizationName} Logo`}
          src={`/${context.logoName}`}
          sx={{
            height: '100%',
            width: '100%',
            objectFit: 'contain',
          }}
        />
      </Button>
      <Stack
        direction="column"
        sx={{
          alignSelf: { xs: 'center', md: 'stretch' },
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="h1"
          sx={{
            textAlign: { xs: 'center', md: 'left' },
            lineHeight: 1,
            fontSize: {
              xs: '1.5rem',
              sm: '1.75rem',
              md: '2rem',
              lg: '2.25rem',
              xl: '2.5rem',
            },
            ml: 1,
          }}
        >
          {context.title}
        </Typography>
        <Tabs
          aria-label="Dashboard Tabs"
          role="navigation"
          scrollButtons={false}
          sx={{
            display: { xs: 'none', md: 'flex' },
            minHeight: 0,
            [`& .${tabClasses.root}:not(.${tabClasses.selected})`]: {
              color: 'inherit',
            },
            [`& .${tabClasses.selected}`]: {
              fontWeight: 'bold',
            },
            [`& .${tabsClasses.indicator}`]: {
              height: 3,
              clipPath: `inset(0 ${TAB_BOX_SHADOW_WIDTH})`,
            },
          }}
          value={context.selectedDashboard}
        >
          {context.tabs.map((tab) => (
            <Tab
              component={Link}
              label={tab.tabName}
              key={tab.id}
              to={`/${tab.slug}`}
              value={tab.slug}
              sx={{
                fontSize: { sm: 13, md: 14, lg: 16 },
                minHeight: 0,
                mx: 1,
                mt: { md: 1.5, lg: 1 },
                mb: { md: 0.5, lg: 1 },
                minWidth: 'auto',
                px: TAB_BOX_SHADOW_WIDTH,
                py: 0.5,
              }}
            />
          ))}
        </Tabs>
      </Stack>

      {/* Hamburger Menu */}
      <Box sx={{ display: { sm: 'flex', md: 'none' } }}>
        <IconButton
          aria-label="Open navigation menu"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
          sx={{ color: 'inherit', mr: 1 }}
        >
          <MenuIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
        </IconButton>
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        >
          <List>
            {context.tabs.map((d) => (
              <ListItem disablePadding disableGutters key={d.id}>
                <ListItemButton
                  component={Link}
                  key={d.id}
                  onClick={() => setDrawerOpen(false)}
                  selected={d.slug === context.selectedDashboard}
                  sx={{ color: 'text.primary' }}
                  to={`/${d.slug}`}
                >
                  <ListItemText primary={d.tabName} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Drawer>
      </Box>
    </Stack>
  );
}
